import { NavLink, useNavigate } from "react-router-dom";
import {
    LayoutDashboard, FolderLock, ShieldCheck, TrendingUp,
    Landmark, Home, Scroll, Users, Siren, Sparkles, LogOut,
    Briefcase, MessageCircle, Heart, BookHeart, Lock, CreditCard, Fingerprint,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { BrandLockup } from "@/components/Brand";

const items = [
    { to: "/dashboard", label: "Overview", icon: LayoutDashboard },
    { to: "/vault", label: "Living Archive", icon: FolderLock },
    { to: "/stories", label: "Stories & Heritage", icon: BookHeart },
    { to: "/insurance", label: "Insurance", icon: ShieldCheck },
    { to: "/investments", label: "Investments", icon: TrendingUp },
    { to: "/accounts", label: "Accounts", icon: Landmark },
    { to: "/assets", label: "Assets", icon: Home },
    { to: "/legal", label: "Legacy Plans", icon: Scroll },
    { to: "/family", label: "Family & Pros", icon: Users },
];

const aiItems = [
    { to: "/ai-chat", label: "Ask LifeVault", icon: MessageCircle },
    { to: "/health-score", label: "Vault Score", icon: Sparkles },
    { to: "/simulation", label: "Legacy Rehearsal", icon: Heart },
    { to: "/pan-snapshot", label: "PAN Snapshot", icon: Fingerprint },
];

export default function Sidebar() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    return (
        <aside
            data-testid="app-sidebar"
            className="flex h-screen w-64 flex-col border-r border-border bg-card sticky top-0"
        >
            <div className="px-5 pt-7 pb-5 border-b border-border">
                <NavLink to="/dashboard" className="block">
                    <BrandLockup size="md" parent={true} module="LifeVault" testid="sidebar-brand" />
                </NavLink>
            </div>

            <nav className="flex-1 px-3 py-4 overflow-y-auto">
                {items.map((it) => (
                    <NavLink
                        key={it.to}
                        to={it.to}
                        data-testid={`nav-${it.to.slice(1)}`}
                        className={({ isActive }) =>
                            `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors duration-200 ${
                                isActive
                                    ? "bg-primary text-primary-foreground"
                                    : "text-foreground/80 hover:bg-secondary hover:text-foreground"
                            }`
                        }
                    >
                        <it.icon className="h-4 w-4" strokeWidth={1.75} />
                        {it.label}
                    </NavLink>
                ))}

                <div className="label-tag mt-6 mb-2 px-3">AI Tools</div>
                {aiItems.map((it) => (
                    <NavLink
                        key={it.to}
                        to={it.to}
                        data-testid={`nav-${it.to.slice(1)}`}
                        className={({ isActive }) =>
                            `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors duration-200 ${
                                isActive
                                    ? "bg-primary text-primary-foreground"
                                    : "text-foreground/80 hover:bg-secondary hover:text-foreground"
                            }`
                        }
                    >
                        <it.icon className="h-4 w-4" strokeWidth={1.75} />
                        {it.label}
                    </NavLink>
                ))}

                {(user?.role === "advisor" || user?.role === "admin") && (
                    <>
                        <NavLink
                            to="/advisor"
                            data-testid="nav-advisor"
                            className={({ isActive }) =>
                                `mt-2 flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors duration-200 ${
                                    isActive
                                        ? "bg-primary text-primary-foreground"
                                        : "text-foreground/80 hover:bg-secondary hover:text-foreground"
                                }`
                            }
                        >
                            <Briefcase className="h-4 w-4" strokeWidth={1.75} />
                            Advisor Console
                        </NavLink>
                        <NavLink
                            to="/billing"
                            data-testid="nav-billing"
                            className={({ isActive }) =>
                                `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors duration-200 ${
                                    isActive
                                        ? "bg-primary text-primary-foreground"
                                        : "text-foreground/80 hover:bg-secondary hover:text-foreground"
                                }`
                            }
                        >
                            <CreditCard className="h-4 w-4" strokeWidth={1.75} />
                            Billing
                        </NavLink>
                    </>
                )}

                <NavLink
                    to="/security"
                    data-testid="nav-security"
                    className={({ isActive }) =>
                        `mt-2 flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors duration-200 ${
                            isActive
                                ? "bg-primary text-primary-foreground"
                                : "text-foreground/80 hover:bg-secondary hover:text-foreground"
                        }`
                    }
                >
                    <Lock className="h-4 w-4" strokeWidth={1.75} />
                    Security
                </NavLink>
            </nav>

            <div className="border-t border-border p-3 space-y-2">
                <button
                    data-testid="crisis-mode-trigger"
                    onClick={() => navigate("/crisis")}
                    className="w-full flex items-center justify-center gap-2 rounded-md bg-accent text-accent-foreground py-2.5 text-sm font-semibold transition-colors duration-200 hover:bg-accent/90"
                >
                    <Siren className="h-4 w-4" />
                    Open Crisis Mode
                </button>
                <button
                    data-testid="logout-btn"
                    onClick={() => {
                        logout();
                        navigate("/");
                    }}
                    className="w-full flex items-center gap-2 rounded-md px-3 py-2 text-xs text-muted-foreground hover:text-foreground"
                >
                    <LogOut className="h-3.5 w-3.5" />
                    Sign out · {user?.email}
                </button>
            </div>
        </aside>
    );
}
